import { ArrowRight, Server, Shield, Cloud, Cpu, BarChart3, Headphones } from "lucide-react";

const services = [
  {
    title: "Infrastructure Planning",
    description: "Design and implement scalable infrastructure solutions that grow with your business needs and budget.",
    icon: Server,
    accent: "sky",
  },
  {
    title: "Cybersecurity",
    description: "Protect your digital assets with advanced threat detection, response, and compliance management.",
    icon: Shield,
    accent: "indigo",
  },
  {
    title: "Cloud Solutions",
    description: "Migrate, optimize, and manage cloud-native applications across AWS, Azure, and GCP environments.",
    icon: Cloud,
    accent: "violet",
  },
  {
    title: "Digital Transformation",
    description: "Navigate your digital journey with comprehensive strategies that modernize operations and culture.",
    icon: Cpu,
    accent: "emerald",
  },
  {
    title: "IT Strategy",
    description: "Align technology investments with business objectives through roadmapping and governance frameworks.",
    icon: BarChart3,
    accent: "orange",
  },
  {
    title: "Technical Support",
    description: "24/7 expert support, monitoring, and maintenance for your entire technology stack.",
    icon: Headphones,
    accent: "pink",
  },
];

const accentMap: Record<string, string> = {
  sky:    "bg-sky-100 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 group-hover:bg-sky-500 group-hover:text-white",
  indigo: "bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-500 group-hover:text-white",
  violet: "bg-violet-100 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 group-hover:bg-violet-500 group-hover:text-white",
  emerald:"bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 group-hover:bg-emerald-500 group-hover:text-white",
  orange: "bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 group-hover:bg-orange-500 group-hover:text-white",
  pink:   "bg-pink-100 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400 group-hover:bg-pink-500 group-hover:text-white",
};

const linkAccentMap: Record<string, string> = {
  sky:    "text-sky-600 dark:text-sky-400",
  indigo: "text-indigo-600 dark:text-indigo-400",
  violet: "text-violet-600 dark:text-violet-400",
  emerald:"text-emerald-600 dark:text-emerald-400",
  orange: "text-orange-600 dark:text-orange-400",
  pink:   "text-pink-600 dark:text-pink-400",
};

export const Services = () => {
  return (
    <section id="what-we-do" className="py-24 px-4 bg-slate-50 dark:bg-slate-900">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16 space-y-4">
          <span className="inline-block text-sm font-medium px-4 py-1.5 rounded-full bg-sky-100 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-500/20">
            Our Expertise
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white">
            What We Do
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Comprehensive technology solutions engineered to help businesses thrive in an ever-evolving digital landscape.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                className="group relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-8 hover:shadow-xl hover:shadow-slate-200/60 dark:hover:shadow-slate-900/60 hover:-translate-y-1 transition-all duration-300"
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 transition-colors duration-300 ${accentMap[service.accent]}`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-slate-900 dark:text-white">
                  {service.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <button
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${linkAccentMap[service.accent]} hover:opacity-80`}
                >
                  Learn More
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
